import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import RestoreIcon from '@mui/icons-material/Restore';
import { appColors } from '../../Config/Theme';
import {
  getUserManagementErrorMessage,
  replaceManagedUserRoles,
  updateManagedUserStatus,
} from '../../Services/UserManagementService';
import {
  ManagedUser,
  UserManagementMetadata,
} from '../../Types/UserManagement';
import { managedUserDisplayName } from '../../Utils/managedUserDisplayName';
import RoleAssignmentEditor, {
  buildRoleAssignmentInputs,
  createRoleAssignmentDrafts,
  RoleAssignmentDrafts,
} from './RoleAssignmentEditor';

interface ManagedUserDrawerProps {
  user: ManagedUser | null;
  metadata: UserManagementMetadata;
  open: boolean;
  onClose: () => void;
  onUpdated: (user: ManagedUser, message: string) => void;
}

const initialsFor = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase() || 'U';

const ManagedUserDrawer: React.FC<ManagedUserDrawerProps> = ({
  user,
  metadata,
  open,
  onClose,
  onUpdated,
}) => {
  const [drafts, setDrafts] = useState<RoleAssignmentDrafts>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmStatus, setConfirmStatus] = useState(false);

  useEffect(() => {
    if (!user) return;
    setDrafts(createRoleAssignmentDrafts(metadata.roles, user.roleAssignments));
    setError(null);
    setSaving(false);
    setConfirmStatus(false);
  }, [metadata.roles, user]);

  const assignments = useMemo(
    () => buildRoleAssignmentInputs(metadata.roles, drafts),
    [drafts, metadata.roles],
  );

  const hasIncompleteLocationRole = metadata.roles.some((role) => {
    const draft = drafts[role.appRoleId];
    return role.canAssign &&
      draft?.selected &&
      !role.requiresGlobalScope &&
      draft.scope === 'locations' &&
      draft.locationIds.length === 0;
  });

  const protectedAssignments = user?.roleAssignments.filter((assignment) => !assignment.canManage) ?? [];
  const displayName = user ? managedUserDisplayName(user.displayName) : '';
  const assignedLocations = user?.roleAssignments
    .filter((assignment) => assignment.locationId && assignment.locationName)
    .reduce<Map<string, string>>((map, assignment) => {
      map.set(assignment.locationId!, assignment.locationName!);
      return map;
    }, new Map()) ?? new Map<string, string>();

  const saveRoles = async () => {
    if (!user || hasIncompleteLocationRole) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await replaceManagedUserRoles(user.appUserId, assignments);
      onUpdated(updated, 'Role assignments saved.');
    } catch (saveError) {
      setError(getUserManagementErrorMessage(saveError, 'Unable to save role assignments.'));
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async () => {
    if (!user) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updateManagedUserStatus(user.appUserId, !user.isActive);
      onUpdated(updated, updated.isActive ? 'EEAS access restored.' : 'EEAS access suspended.');
      setConfirmStatus(false);
    } catch (statusError) {
      setError(getUserManagementErrorMessage(statusError, 'Unable to change user status.'));
      setConfirmStatus(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={saving ? undefined : onClose}
        slotProps={{
          paper: {
            sx: {
              width: { xs: '100%', sm: 440 },
              maxWidth: '100vw',
              p: 0,
            },
          },
        }}
      >
        {user && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                <IconButton aria-label="Close user details" onClick={onClose} disabled={saving}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: appColors.primary.main,
                    fontSize: '1.2rem',
                    fontWeight: 700,
                  }}
                >
                  {initialsFor(displayName)}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>
                    {displayName}
                  </Typography>
                  <Typography variant="body2" sx={{ color: appColors.text.body }} noWrap>
                    {user.email || user.userName}
                  </Typography>
                  <Chip
                    label={user.isActive ? 'Access enabled' : user.isAccessSuspended ? 'Access suspended' : 'Inactive'}
                    size="small"
                    sx={{
                      mt: 0.75,
                      bgcolor: user.isActive ? appColors.status.successBg : '#FFEBEE',
                      color: user.isActive ? appColors.status.success : appColors.status.error,
                      fontWeight: 700,
                    }}
                  />
                  {user.hrStatus && (
                    <Chip
                      label={`HR: ${user.hrStatus === 'A' ? 'Active' : user.hrStatus === 'T' ? 'Terminated' : user.hrStatus}`}
                      size="small"
                      variant="outlined"
                      color={user.hrStatus === 'A' ? 'success' : user.hrStatus === 'T' ? 'error' : 'default'}
                      sx={{ mt: 0.75, ml: 0.75 }}
                    />
                  )}
                </Box>
              </Box>
            </Box>
            <Divider />

            <Box sx={{ px: 3, py: 2.25, overflowY: 'auto', flex: 1 }}>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Identity
              </Typography>
              <Box
                sx={{
                  border: `1px solid ${appColors.border.default}`,
                  borderRadius: '10px',
                  px: 1.5,
                  py: 1.25,
                  mb: 2.5,
                  bgcolor: appColors.surface.page,
                }}
              >
                <Typography variant="caption" sx={{ color: appColors.text.caption }}>
                  Username
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.75 }}>{user.userName}</Typography>
                <Typography variant="caption" sx={{ color: appColors.text.caption }}>
                  External identity reference
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                  {user.externalUserId}
                </Typography>
                <Typography variant="caption" sx={{ color: appColors.text.caption, display: 'block', mt: 0.75 }}>
                  Exponent employee ID
                </Typography>
                <Typography variant="body2">
                  {user.expdwEmployeeId || 'Not linked'}
                </Typography>
                <Typography variant="caption" sx={{ color: appColors.text.caption, display: 'block', mt: 0.75 }}>
                  HR status last synchronized
                </Typography>
                <Typography variant="body2">
                  {user.hrSyncedAt
                    ? new Intl.DateTimeFormat(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    }).format(new Date(user.hrSyncedAt))
                    : 'Not synchronized'}
                </Typography>
              </Box>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Assigned locations
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2.5 }}>
                {Array.from(assignedLocations.entries()).map(([locationId, locationName]) => (
                  <Chip
                    key={locationId}
                    icon={<LocationOnOutlinedIcon />}
                    label={locationName}
                    size="small"
                    sx={{ bgcolor: 'rgba(46, 139, 132, 0.10)', color: appColors.primary.dark }}
                  />
                ))}
                {assignedLocations.size === 0 && (
                  <Typography variant="body2" sx={{ color: appColors.text.caption }}>
                    Global access only or no location assignments.
                  </Typography>
                )}
              </Box>

              {protectedAssignments.length > 0 && (
                <Box sx={{ mb: 2.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Protected assignments
                  </Typography>
                  <Alert severity="info" icon={<LockOutlinedIcon />} sx={{ mb: 1 }}>
                    These assignments are visible but outside your management authority.
                  </Alert>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {protectedAssignments.map((assignment) => (
                      <Chip
                        key={assignment.appUserRoleId}
                        label={`${assignment.roleName}${assignment.locationName ? ` · ${assignment.locationName}` : ' · Global'}`}
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Role assignments
              </Typography>
              <Typography variant="caption" sx={{ color: appColors.text.caption, display: 'block', mb: 1.25 }}>
                Changes apply to standing EEAS access and future incident team snapshots.
              </Typography>
              <RoleAssignmentEditor
                roles={metadata.roles}
                locations={metadata.locations}
                capabilities={metadata.capabilities}
                drafts={drafts}
                onChange={setDrafts}
                disabled={saving || !user.canManage}
              />
              {hasIncompleteLocationRole && (
                <Typography variant="caption" sx={{ color: appColors.status.error, mt: 1, display: 'block' }}>
                  Select at least one location for each location-scoped role.
                </Typography>
              )}
            </Box>

            <Divider />
            <Box sx={{ px: 3, py: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                variant="contained"
                fullWidth
                onClick={saveRoles}
                disabled={saving || !user.canManage || hasIncompleteLocationRole}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </Button>
              {user.canActivateDeactivate && (
                <Button
                  variant="outlined"
                  color={user.isActive ? 'error' : 'primary'}
                  fullWidth
                  onClick={() => setConfirmStatus(true)}
                  disabled={saving}
                  startIcon={user.isActive ? <DeleteOutlineIcon /> : <RestoreIcon />}
                >
                  {user.isActive ? 'Suspend EEAS Access' : 'Restore EEAS Access'}
                </Button>
              )}
            </Box>
          </Box>
        )}
      </Drawer>

      <Dialog open={confirmStatus} onClose={() => setConfirmStatus(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{user?.isActive ? 'Suspend EEAS access?' : 'Restore EEAS access?'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {user?.isActive
              ? `${displayName} will immediately lose EEAS application access. Their role assignments and HR status will be retained.`
              : `${displayName} will regain EEAS access with their retained role assignments, provided their HR status remains active.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmStatus(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            color={user?.isActive ? 'error' : 'primary'}
            onClick={changeStatus}
            disabled={saving}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ManagedUserDrawer;
